import type {IssueViewerFeed} from '@github-ui/preheat-issues-manager/issue-viewer-storage-key'
import {IssueCommentItems, IssueTimelineItems} from '@github-ui/server-persisted-queries'
import type {IssueCommentItemsQuery} from '@github-ui/server-persisted-queries/types/IssueCommentItems'
import type {
  IssueTimelineItemsItemType,
  IssueTimelineItemsQuery,
} from '@github-ui/server-persisted-queries/types/IssueTimelineItems'
import {verifiedFetchJSON} from '@github-ui/verified-fetch'

/**
 * The cached value is the issue node, not the bare connection: the optimistic-connection engine
 * locates a connection's owner by matching `id` inside the cached document.
 */
type ActivityTimelineIssue = NonNullable<NonNullable<IssueTimelineItemsQuery['repository']>['issue']>
type CommentsTimelineIssue = NonNullable<NonNullable<IssueCommentItemsQuery['repository']>['issue']>
export type TimelineIssue = ActivityTimelineIssue | CommentsTimelineIssue
export type TimelineFeed = IssueViewerFeed
/** Narrows the activity feed to these item types. Unset reads every type. */
export type TimelineItemTypes = readonly IssueTimelineItemsItemType[]

export type TimelineItemsConnection = NonNullable<TimelineIssue['timelineItems']>

/** The path from the cached issue node to the connection, for `applyOperation`. */
export const TIMELINE_CONNECTION_PATH: string[] = ['timelineItems']
export type TimelineItemsPageNode = NonNullable<NonNullable<TimelineItemsConnection['edges']>[number]>['node']

export type TimelineItemsPageArgs = {
  owner: string
  repo: string
  number: number
  feed?: TimelineFeed
  itemTypes?: TimelineItemTypes
} & TimelineItemsPageParams

/**
 * Forward (`first`/`after`), backward (`last`/`before`) and deep-link (`focusText`) reads all come
 * from the one endpoint, matching the three Relay documents this replaces.
 */
export type TimelineItemsPageParams = {
  first?: number
  after?: string
  last?: number
  before?: string
  timelinePage?: number
  focusText?: string
  focusNeighborCount?: number
}

// `itemTypes` is left out when unset so unfiltered keys stay as they were. Prefixes match keys
// partially, so a prefix without `itemTypes` still reaches every filtered read of the feed.
function feedKey(owner: string, repo: string, number: number, feed: TimelineFeed, itemTypes?: TimelineItemTypes) {
  return itemTypes ? {owner, repo, number, feed, itemTypes} : {owner, repo, number, feed}
}

/** Prefix matching every window and cursor page for one issue. */
export function timelineItemsQueryKeyPrefix(
  owner: string,
  repo: string,
  number: number,
  feed: TimelineFeed = 'activity',
  itemTypes?: TimelineItemTypes,
) {
  return ['issue-viewer', 'timeline-items', feedKey(owner, repo, number, feed, itemTypes)] as const
}

export function timelineItemsQueryKey(
  owner: string,
  repo: string,
  number: number,
  params?: TimelineItemsPageParams,
  feed: TimelineFeed = 'activity',
  itemTypes?: TimelineItemTypes,
) {
  return ['issue-viewer', 'timeline-items', feedKey(owner, repo, number, feed, itemTypes), params ?? {}] as const
}

export function timelineReadIncludes(queryKey: readonly unknown[], itemType: IssueTimelineItemsItemType): boolean {
  const {itemTypes} = (queryKey[2] ?? {}) as {itemTypes?: TimelineItemTypes}
  return !itemTypes || itemTypes.includes(itemType)
}

const PARAM_NAMES: Record<keyof TimelineItemsPageParams, string> = {
  first: 'first',
  after: 'after',
  last: 'last',
  before: 'before',
  timelinePage: 'timeline_page',
  focusText: 'focus_text',
  focusNeighborCount: 'focus_neighbor_count',
}

export function timelineItemsUrl({
  owner,
  repo,
  number,
  feed = 'activity',
  itemTypes,
  ...params
}: TimelineItemsPageArgs): string {
  const comments = feed === 'comments'
  const search = new URLSearchParams({queryId: comments ? IssueCommentItems : IssueTimelineItems})

  for (const [key, name] of Object.entries(PARAM_NAMES)) {
    const value = params[key as keyof TimelineItemsPageParams]
    if (value !== undefined) search.set(name, String(value))
  }
  if (!comments && itemTypes) search.set('item_types', itemTypes.join(','))

  const endpoint = comments ? 'comment_items' : 'timeline_items'
  return `/${owner}/${repo}/issues/${number}/${endpoint}?${search}`
}

export async function fetchTimelineItems(args: TimelineItemsPageArgs, signal?: AbortSignal): Promise<TimelineIssue> {
  const response = await verifiedFetchJSON(timelineItemsUrl(args), {signal})
  if (!response.ok) {
    throw new Error(`Timeline items fetch failed (${response.status})`)
  }

  const json = (await response.json()) as {
    data?: IssueTimelineItemsQuery | IssueCommentItemsQuery
    errors?: unknown[]
  }
  const issue = json.data?.repository?.issue
  if (!issue?.timelineItems) {
    throw new Error('Timeline items query returned no data')
  }

  return issue
}

export function timelineQueryOptions(
  owner: string,
  repo: string,
  number: number,
  params: TimelineItemsPageParams,
  feed: TimelineFeed = 'activity',
  itemTypes?: TimelineItemTypes,
) {
  return {
    queryKey: timelineItemsQueryKey(owner, repo, number, params, feed, itemTypes),
    queryFn: ({signal}: {signal?: AbortSignal}) =>
      fetchTimelineItems({owner, repo, number, feed, itemTypes, ...params}, signal),
    throwOnError: true as const,
  }
}

export type TimelineQueryOptions = ReturnType<typeof timelineQueryOptions>
